import { AddMediaElementCommand } from './AddMediaElementCommand';
import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { Track } from '../models/Track';
import { TimelineMode } from './TimelineMode';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';

export interface AddMusicElementOptions {
  /**
   * If true, adjusts the music duration to fit from its start time to the end of the timeline
   */
  fitToEnd?: boolean;
  
  /**
   * Default volume for the music element (0-100)
   */
  volume?: number;
  
  /**
   * Whether to automatically add fade in/out effects
   */
  autoFade?: boolean;
}

/**
 * Specialized command for adding music/audio elements to the timeline.
 * Extends AddMediaElementCommand with music-specific features:
 * - Fit to end option: automatically adjusts duration to fill to timeline end
 * - Volume control: sets initial volume level
 * - Auto fade: applies fade in/out effects
 * - Smart track placement: fills gaps in existing music tracks before creating new ones
 */
export class AddMusicElementCommand extends AddMediaElementCommand {
  private options: AddMusicElementOptions;
  private originalDuration?: number;
  private calculatedDuration?: number;

  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode: TimelineMode = TimelineMode.DEFAULT,
    options: AddMusicElementOptions = {}
  ) {
    super(editor, element, targetTrackId, mode);
    this.options = {
      fitToEnd: false,
      volume: 10, // Default volume from original implementation
      autoFade: false,
      ...options,
    };
    
    // Store original duration before any modifications
    this.originalDuration = element.durationSeconds;
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
      options: this.options,
    });

    // Apply music-specific modifications before executing parent logic
    if (!this.applyMusicSpecificModifications()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply music-specific modifications',
      });
      return false;
    }

    // Call parent execute which handles the core adding logic
    return super.execute();
  }

  undo(): boolean {
    // Restore original duration if it was modified
    if (this.originalDuration !== undefined && this.calculatedDuration !== undefined) {
      this.element.update({ duration: this.originalDuration });
    }

    // Call parent undo logic
    return super.undo();
  }

  getDescription(): string {
    const baseDescription = super.getDescription();
    const musicFeatures: string[] = [];
    
    if (this.options.fitToEnd) {
      musicFeatures.push('fit-to-end');
    }
    if (this.options.volume !== undefined && this.options.volume !== 10) {
      musicFeatures.push(`volume: ${this.options.volume}`);
    }
    if (this.options.autoFade) {
      musicFeatures.push('auto-fade');
    }

    if (musicFeatures.length > 0) {
      return `${baseDescription} [${musicFeatures.join(', ')}]`;
    }

    return baseDescription;
  }

  /**
   * Gets the calculated duration if fit-to-end was applied
   */
  getCalculatedDuration(): number | undefined {
    return this.calculatedDuration;
  }

  /**
   * Gets the original duration before any modifications
   */
  getOriginalDuration(): number | undefined {
    return this.originalDuration;
  }

  /**
   * Override the findOrCreateTargetTrack method to implement music-specific logic
   */
  protected findOrCreateTargetTrack(): Track | undefined {
    // If specific track ID provided, use it (unless it has collisions in DEFAULT mode)
    if (this.targetTrackId) {
      const track = this.editor.getTrackById(this.targetTrackId);
      if (track) {
        // In DEFAULT mode, if target track has collision, create new track
        if (this.mode === TimelineMode.DEFAULT && this.hasCollision(track)) {
          VideoEditorLogger.logTimelineOperation('add_music_element_collision', {
            message: `Target track ${this.targetTrackId} has collision, creating new track`,
          });
          return this.createNewTrack();
        }
        return track;
      }
      VideoEditorLogger.logTimelineOperation('add_music_element_track_not_found', {
        warning: `Target track ${this.targetTrackId} not found, will auto-select`,
      });
    }

    // Auto-select compatible track based on music-specific logic
    const compatibleTrack = this.findCompatibleMusicTrack();
    if (compatibleTrack) {
      return compatibleTrack;
    }

    // Create new track if no compatible track found
    return this.createNewTrack();
  }

  /**
   * Music-specific track finding logic to prefer audio tracks and handle gap filling
   */
  private findCompatibleMusicTrack(): Track | undefined {
    // Only look for audio tracks for music elements
    const audioTracks = this.editor.timeline.tracks.filter(
      (track) => track.type === 'audio'
    );

    if (audioTracks.length === 0) {
      return undefined;
    }

    // For DEFAULT mode, try to find a track with space for the music
    if (this.mode === TimelineMode.DEFAULT) {
      // First, try to find a track where we can fit without collisions
      for (const track of audioTracks) {
        if (!this.hasCollision(track)) {
          return track;
        }
      }

      // If all tracks have collisions, try to find a track where we can fill a gap
      const trackWithSpace = this.findTrackWithSpace(audioTracks);
      if (trackWithSpace) {
        return trackWithSpace;
      }

      // No suitable tracks found, will create new track
      return undefined;
    }

    // For RIPPLE and OVERLAP modes, use the first audio track
    return audioTracks[0];
  }

  /**
   * Applies music-specific modifications to the element
   */
  private applyMusicSpecificModifications(): boolean {
    try {
      const modifications: Record<string, any> = {};

      // Apply volume setting (as number for AudioElement.update to convert)
      if (this.options.volume !== undefined) {
        modifications.volume = this.options.volume;
      }

      // Apply fit-to-end logic
      if (this.options.fitToEnd) {
        const fittedDuration = this.calculateFitToEndDuration();
        if (fittedDuration === null) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Cannot fit music to end - insufficient space available',
          });
          return false;
        }
        modifications.duration = fittedDuration;
        this.calculatedDuration = fittedDuration;
      }

      // Apply auto-fade effects (use Creatomate property names)
      if (this.options.autoFade) {
        modifications.audio_fade_in = 1.0; // 1 second fade in
        modifications.audio_fade_out = 1.0; // 1 second fade out
      }

      // Apply all modifications at once using the proper update method
      if (Object.keys(modifications).length > 0) {
        this.element.update(modifications);
        
        VideoEditorLogger.logTimelineOperation('add_music_element_modifications_applied', {
          action: 'applied_music_modifications',
          modifications,
        });
      }

      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply music modifications',
        details: error,
      });
      return false;
    }
  }

  /**
   * Calculates the duration needed to fit the music from its start time to the end of the timeline
   */
  private calculateFitToEndDuration(): number | null {
    const elementStartTime = this.element.time;
    const timelineEndTime = this.calculateTimelineEndTime();
    
    if (timelineEndTime <= elementStartTime) {
      return null; // No space available
    }

    const availableDuration = timelineEndTime - elementStartTime;
    const minimumDuration = 1.0; // Minimum 1 second

    if (availableDuration < minimumDuration) {
      return null; // Not enough space for meaningful music
    }

    VideoEditorLogger.logTimelineOperation('add_music_element_fit_to_end_calculated', {
      action: 'calculated_fit_to_end_duration',
      elementStartTime,
      timelineEndTime,
      availableDuration,
    });

    return availableDuration;
  }

  /**
   * Calculates the end time of the timeline based on all existing elements
   */
  private calculateTimelineEndTime(): number {
    let maxEndTime = 0;

    for (const track of this.editor.timeline.tracks) {
      for (const element of track.elements) {
        const elementEndTime = element.time + element.durationSeconds;
        maxEndTime = Math.max(maxEndTime, elementEndTime);
      }
    }

    // If no elements exist, use a default duration
    return maxEndTime > 0 ? maxEndTime : 30; // Default to 30 seconds
  }

  /**
   * Finds an audio track that has space to accommodate the music element
   */
  private findTrackWithSpace(audioTracks: Track[]): Track | undefined {
    const elementDuration = this.element.durationSeconds;
    const elementStartTime = this.element.time;
    const elementEndTime = elementStartTime + elementDuration;

    for (const track of audioTracks) {
      // Check if there's a gap in this track that can fit our element
      if (this.canFitInTrackGaps(track, elementStartTime, elementEndTime)) {
        return track;
      }
    }

    return undefined;
  }

  /**
   * Checks if the element can fit in the gaps of a track
   */
  private canFitInTrackGaps(track: Track, startTime: number, endTime: number): boolean {
    // Sort elements by start time
    const sortedElements = [...track.elements].sort((a, b) => a.time - b.time);

    // Check if we can fit before the first element
    if (sortedElements.length === 0 || endTime <= sortedElements[0].time) {
      return true;
    }

    // Check gaps between elements
    for (let i = 0; i < sortedElements.length - 1; i++) {
      const currentElementEnd = sortedElements[i].time + sortedElements[i].durationSeconds;
      const nextElementStart = sortedElements[i + 1].time;

      if (startTime >= currentElementEnd && endTime <= nextElementStart) {
        return true;
      }
    }

    // Check if we can fit after the last element
    const lastElement = sortedElements[sortedElements.length - 1];
    const lastElementEnd = lastElement.time + lastElement.durationSeconds;
    if (startTime >= lastElementEnd) {
      return true;
    }

    return false;
  }

  /**
   * Override parent's collision detection to use music-specific logic
   */
  protected hasCollision(track: Track): boolean {
    // In music mode, we want to check for gaps as well as collisions
    if (this.mode === TimelineMode.DEFAULT) {
      // Use gap-aware collision detection
      return !this.canFitInTrackGaps(
        track,
        this.element.time,
        this.element.time + this.element.durationSeconds
      );
    }

    // For other modes, use parent collision detection
    return super.hasCollision(track);
  }

  /**
   * Creates a new audio track specifically for music
   */
  protected createNewTrack(): Track | undefined {
    try {
      const trackManager = this.editor.getTrackManager();
      this.createdTrack = trackManager.createTrack('audio');
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation('add_music_element_track_created', {
        action: 'audio_track_created',
        trackId: this.createdTrack.id,
        trackType: 'audio',
      });

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to create new audio track',
        details: error,
      });
      return undefined;
    }
  }
}